/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Reset Password Page — a forwarding page, nothing more.
 *
 * N-4 / D-5 (S620): this page used to be titled "Reset Your Password", render a
 * full password form with validation and a strength indicator, and then, on
 * submit, throw away everything the customer had typed and send them to the
 * Deskshare website to type it again. Asking for something you cannot accept is
 * worse than asking for nothing: the customer chose a password, confirmed it,
 * pressed the button, and was made to start over somewhere else.
 *
 * TA-5 (S576) established the rule this now follows: the shop does not set
 * passwords. Password reset and password creation both belong to the Deskshare
 * website, which owns the credential. Keeping a form here would mean two places
 * that can change one password.
 *
 * D-5: redirect, do not delete. Older purchase e-mails and older links still
 * point at this address, and a customer following one must land somewhere useful
 * rather than on a Not Found page. Revisit deleting it only once no link older
 * than this change is plausibly still in use.
 */

import React from "react";
// P-5 (S629): the secondary flows read the engine directly. Each of these
// pages mixes components with plain helper functions that build the same
// wording, and i18n.t resolves at render time in both. Nothing here is a
// module-level constant, which is the one shape that would freeze the words
// in English before the chosen language finished loading.
import ErrorBoundary from "@/components/ErrorBoundary";
import { useLangPrefix } from "@/hooks/use-lang-prefix";
import { buildPasswordResetUrl } from "@/lib/auth-redirect";
import { useSearchParams } from "react-router-dom";
import { useDocumentTitle } from "@/hooks/use-document-title";
import i18n from "@/i18n";

const ResetPasswordPage = () => {
  useDocumentTitle(i18n.t("resetPassword.pageTitle"));
  const { pagePath } = useLangPrefix();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  // N-4: forward on ARRIVAL, not on submit. The page's only job is to hand the
  // customer to the page that can actually change their password.
  //
  // window.location.replace, not assign: the shop page must not sit in the
  // browser history, or pressing Back after signing in returns the customer here
  // and forwards them again — a loop they cannot get out of.
  React.useEffect(() => {
    if (!token) return;
    window.location.replace(buildPasswordResetUrl(token));
  }, [token]);

  // Guard: if there is no token the reset cannot succeed — show a clear message.
  // Kept deliberately: a visit with no token is the one case this page can
  // usefully answer itself.
  if (!token) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-md p-8 w-full max-w-md m-4 text-center">
          <div className="bg-yellow-100 rounded-full w-16 h-16 flex items-center justify-center mb-4 mx-auto">
            <svg width="40" height="40" fill="none" viewBox="0 0 24 24">
              <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-yellow-500" />
            </svg>
          </div>
          <h2 className="font-bold text-2xl mb-2">{i18n.t("resetPassword.invalidLinkTitle")}</h2>
          <p className="text-gray-500 text-sm mb-6">{i18n.t("resetPassword.invalidLinkDescription")}</p>
          <button
            type="button"
            onClick={() => window.location.href = pagePath("")}
            className="w-full bg-primary text-primary-foreground rounded-md py-3 font-semibold text-base hover:bg-btn-primary-hover active:bg-btn-primary-selected transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >

            {i18n.t("resetPassword.backToLogin")}
          </button>
        </div>
      </div>
    );
  }

  // One line, shown only for the moment the browser takes to navigate. role and
  // aria-live announce it to a screen reader, which would otherwise be told
  // nothing at all before the page changes underneath it.
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-md p-8 w-full max-w-md m-4 text-center">
        <p className="text-gray-600 text-base" role="status" aria-live="polite">{i18n.t("resetPassword.redirecting")}</p>
      </div>
    </div>
  );
};

const ResetPasswordPageWithErrorBoundary: React.FC = () => (
  <ErrorBoundary>
    <ResetPasswordPage />
  </ErrorBoundary>
);

export default ResetPasswordPageWithErrorBoundary;
